import { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { parsePeopleSheet, downloadTemplate, type ParsedRow, type ParseResult } from '@/lib/admin/people-template';
import { stageInvitations } from '@/lib/admin/people-api';
import { Download, Upload, FileText, AlertCircle, CheckCircle2, X, Loader2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

export function BulkUploadDialog({ orgId, open, onOpenChange }: { orgId: string; open: boolean; onOpenChange: (v: boolean) => void }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [parsing, setParsing] = useState(false);
  const [pasted, setPasted] = useState('');

  const reset = () => { setParseResult(null); setPasted(''); };

  const handleFile = async (file: File) => {
    setParsing(true);
    try {
      const r = await parsePeopleSheet(file);
      setParseResult(r);
    } catch (e: any) {
      toast({ title: 'Could not parse file', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setParsing(false);
    }
  };

  const handlePaste = async () => {
    if (!pasted.trim()) return;
    setParsing(true);
    try {
      const r = await parsePeopleSheet(pasted);
      setParseResult(r);
    } catch (e: any) {
      toast({ title: 'Could not parse pasted data', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setParsing(false);
    }
  };

  const summary = useMemo(() => {
    if (!parseResult) return null;
    const total = parseResult.rows.length;
    const errors = parseResult.rows.filter((r) => r.errors.length > 0).length;
    const warns = parseResult.rows.filter((r) => r.warnings.length > 0 && r.errors.length === 0).length;
    const ok = total - errors;
    return { total, errors, warns, ok };
  }, [parseResult]);

  const commit = useMutation({
    mutationFn: async () => {
      if (!parseResult) return [];
      const valid = parseResult.rows.filter((r) => r.errors.length === 0);
      return stageInvitations(orgId, valid.map((r) => ({ email: r.email, bands: r.bands })));
    },
    onSuccess: (res) => {
      const staged = res.filter((r) => r.status === 'staged').length;
      const merged = res.filter((r) => r.status === 'merged').length;
      toast({
        title: 'Done',
        description: `${staged} invitation${staged === 1 ? '' : 's'} staged, ${merged} existing ${merged === 1 ? 'person' : 'people'} updated.`,
      });
      qc.invalidateQueries({ queryKey: ['admin', 'people', orgId] });
      qc.invalidateQueries({ queryKey: ['admin', 'pending', orgId] });
      reset();
      onOpenChange(false);
    },
    onError: (e: any) => toast({ title: 'Commit failed', description: e?.message ?? '', variant: 'destructive' }),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk upload people</DialogTitle>
          <DialogDescription>
            Upload an .xlsx or .csv, or paste CSV directly. Nothing is written until you commit.
          </DialogDescription>
        </DialogHeader>

        {!parseResult && (
          <div className="space-y-4">
            <Alert>
              <FileText className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between gap-3">
                <span>Download the template to see the exact columns and a worked example.</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => downloadTemplate('xlsx')}>
                    <Download className="h-3 w-3 mr-1" /> .xlsx
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadTemplate('csv')}>
                    <Download className="h-3 w-3 mr-1" /> .csv
                  </Button>
                </div>
              </AlertDescription>
            </Alert>

            <div className="border-2 border-dashed border-border rounded-md p-6 text-center">
              <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
              <input
                id="bulk-file"
                type="file"
                accept=".xlsx,.csv"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.currentTarget.value = ''; }}
              />
              <label htmlFor="bulk-file">
                <Button asChild variant="default" size="sm" disabled={parsing}>
                  <span>{parsing ? 'Parsing…' : 'Choose file'}</span>
                </Button>
              </label>
              <p className="text-xs text-muted-foreground mt-2">.xlsx or .csv, parsed in your browser</p>
            </div>

            <div>
              <p className="text-sm font-medium mb-1">Or paste CSV</p>
              <Textarea
                placeholder={'email,department,team,location,level,gender,tenure_years\njane@co.com,Eng,Platform,London,IC4,female,3'}
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                rows={4}
                className="font-mono text-xs"
              />
              <Button size="sm" variant="outline" className="mt-2" disabled={!pasted.trim() || parsing} onClick={handlePaste}>
                Parse pasted data
              </Button>
            </div>
          </div>
        )}

        {parseResult && summary && (
          <div className="space-y-3">
            {parseResult.headerError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{parseResult.headerError}</AlertDescription>
              </Alert>
            )}
            {parseResult.unknownColumns.length > 0 && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Ignoring unknown columns: {parseResult.unknownColumns.join(', ')}
                </AlertDescription>
              </Alert>
            )}
            <div className="flex gap-4 text-sm">
              <span><strong>{summary.total}</strong> rows</span>
              <span className="text-success">{summary.ok} ready</span>
              {summary.errors > 0 && <span className="text-destructive">{summary.errors} blocked</span>}
              {summary.warns > 0 && <span className="text-amber-600">{summary.warns} warnings</span>}
            </div>

            <div className="border border-border rounded-md max-h-[40vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 w-8">#</th>
                    <th className="text-left p-2">Email</th>
                    <th className="text-left p-2">Bands</th>
                    <th className="text-left p-2 w-20">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parseResult.rows.map((r) => (
                    <tr key={r.rowNumber} className="border-t border-border">
                      <td className="p-2 text-muted-foreground">{r.rowNumber + 1}</td>
                      <td className="p-2 font-mono truncate max-w-[200px]">{r.email || '—'}</td>
                      <td className="p-2 text-muted-foreground">
                        {r.bands.length === 0 ? '—' : r.bands.map((b) => `${b.dimension}: ${b.label}`).join(', ')}
                      </td>
                      <td className="p-2">
                        {r.errors.length > 0 ? (
                          <span className="text-destructive flex items-center gap-1" title={r.errors.join('; ')}>
                            <X className="h-3 w-3" /> error
                          </span>
                        ) : r.warnings.length > 0 ? (
                          <span className="text-amber-600 flex items-center gap-1" title={r.warnings.join('; ')}>
                            <AlertCircle className="h-3 w-3" /> warn
                          </span>
                        ) : (
                          <span className="text-success flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> ok
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          {parseResult ? (
            <>
              <Button variant="ghost" onClick={reset}>Start over</Button>
              <Button
                disabled={summary?.ok === 0 || commit.isPending || !!parseResult.headerError}
                onClick={() => commit.mutate()}
              >
                {commit.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Stage {summary?.ok} invitation{summary?.ok === 1 ? '' : 's'}
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
